import { useLanguage } from "@/i18n/LanguageContext";

interface ReviewFiltersProps {
  serviceFilter: string;
  setServiceFilter: (v: string) => void;
  neighborhoodFilter: string;
  setNeighborhoodFilter: (v: string) => void;
  photosOnly: boolean;
  setPhotosOnly: (v: boolean) => void;
  onClear: () => void;
  isMobile?: boolean;
  onCloseMobile?: () => void;
}

const serviceMap = [
  { value: "All", key: "reviews.filterAll" },
  { value: "Sliding Door Repair", key: "reviews.filterSliding" },
  { value: "Glass Replacement", key: "reviews.filterGlass" },
  { value: "Window Hardware Repair", key: "reviews.filterHardware" },
  { value: "Weatherstripping & Sealing", key: "reviews.filterWeather" },
  { value: "Window Cleaning & Maintenance", key: "reviews.filterMaintenance" },
  { value: "Emergency Board-Up", key: "reviews.filterEmergency" },
];

const neighborhoods = [
  "All",
  "Brickell",
  "Aventura",
  "Miami Beach",
  "Coral Gables",
  "Coconut Grove",
  "Doral",
  "Kendall",
  "Pinecrest",
  "Bay Harbor Islands",
  "Key Biscayne",
  "South Miami",
  "North Miami Beach",
];

export default function ReviewFilters({
  serviceFilter,
  setServiceFilter,
  neighborhoodFilter,
  setNeighborhoodFilter,
  photosOnly,
  setPhotosOnly,
  onClear,
  isMobile,
  onCloseMobile,
}: ReviewFiltersProps) {
  const { t } = useLanguage();

  return (
    <div className="bg-white border border-stone-100 rounded-xl p-5">
      {isMobile && (
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-stone-100">
          <h3 className="text-base font-bold text-secondary">{t("reviews.filterTitle")}</h3>
          <button
            onClick={onCloseMobile}
            className="w-8 h-8 flex items-center justify-center text-secondary cursor-pointer"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>
      )}
      {!isMobile && <h3 className="text-base font-bold text-secondary mb-4">{t("reviews.filterTitle")}</h3>}

      {/* Service */}
      <div className="mb-5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-2">{t("reviews.byService")}</h4>
        <div className="space-y-1.5">
          {serviceMap.map((s) => (
            <label key={s.value} className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name={isMobile ? "service-mobile" : "service"}
                checked={serviceFilter === s.value}
                onChange={() => setServiceFilter(s.value)}
                className="w-4 h-4 accent-primary cursor-pointer"
              />
              <span
                className={`text-sm ${
                  serviceFilter === s.value
                    ? "text-secondary font-medium"
                    : "text-stone-600 group-hover:text-secondary"
                }`}
              >
                {t(s.key)}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Neighborhood */}
      <div className="mb-5">
        <h4 className="text-xs font-bold uppercase tracking-wider text-stone-400 mb-2">
          {t("reviews.byNeighborhood")}
        </h4>
        <div className="space-y-1.5">
          {neighborhoods.map((n) => (
            <label key={n} className="flex items-center gap-2 cursor-pointer group">
              <input
                type="radio"
                name={isMobile ? "neighborhood-mobile" : "neighborhood"}
                checked={neighborhoodFilter === n}
                onChange={() => setNeighborhoodFilter(n)}
                className="w-4 h-4 accent-primary cursor-pointer"
              />
              <span
                className={`text-sm ${
                  neighborhoodFilter === n
                    ? "text-secondary font-medium"
                    : "text-stone-600 group-hover:text-secondary"
                }`}
              >
                {n === "All" ? t("reviews.filterAll") : n}
              </span>
            </label>
          ))}
        </div>
      </div>

      {/* Photos toggle */}
      <div className="mb-5">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={photosOnly}
            onChange={(e) => setPhotosOnly(e.target.checked)}
            className="w-4 h-4 accent-primary cursor-pointer"
          />
          <span className={`text-sm ${photosOnly ? "text-secondary font-medium" : "text-stone-600"}`}>
            {t("reviews.photosOnly")}
          </span>
        </label>
      </div>

      {/* Clear */}
      <button
        onClick={onClear}
        className="text-sm font-medium text-accent hover:text-accent/80 transition-colors cursor-pointer"
      >
        {t("reviews.clearFilters")}
      </button>
    </div>
  );
}